import { Center, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AxioLoader } from "../../components/AxioLoader";
import { AuthShell } from "../../components/AuthShell";
import { ACCOUNT_COPY } from "../../constants/account";
import { AUTH_IDENTITY_COPY } from "../../lib/kyc";
import { useAuth } from "../../context/useAuth";
import { authApi } from "../../lib/api";
import { ApiError } from "../../lib/apiClient";
import { consumeAppleCallbackParams } from "../../lib/appleAuth";
import { setSignupSession } from "../../lib/googleSignupSession";
import { resolveHomePath } from "../../lib/roles";

export function AppleCallbackPage() {
  const { applyTokenUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    async function finish() {
      try {
        const callback = consumeAppleCallbackParams(`?${searchParams.toString()}`);
        window.history.replaceState(null, "", window.location.pathname);
        const res = await authApi.apple({
          id_token: callback.idToken,
          authorization_code: callback.authorizationCode,
          full_name: callback.fullName,
        });
        if (res.access_token && res.user) {
          applyTokenUser(res.access_token, res.refresh_token ?? "", res.user);
          if (res.account_deletion_recovered) {
            notifications.show({
              color: "green",
              title: ACCOUNT_COPY.deleteRecoveryTitle,
              message: res.message ?? ACCOUNT_COPY.deleteRecoveryBody,
            });
          }
          navigate(await resolveHomePath(res.user), { replace: true });
          return;
        }
        if (res.status === "pending_approval") {
          notifications.show({
            color: "yellow",
            title: AUTH_IDENTITY_COPY.pendingAccountTitle,
            message: AUTH_IDENTITY_COPY.pendingAccountBody,
          });
          navigate("/join", { replace: true });
          return;
        }
        if (res.needs_role_selection && res.apple_signup_token) {
          setSignupSession({ provider: "apple", token: res.apple_signup_token });
          notifications.show({
            color: "blue",
            title: AUTH_IDENTITY_COPY.appleConnectedTitle,
            message: AUTH_IDENTITY_COPY.appleConnectedBody,
          });
          navigate(callback.returnPath.startsWith("/join") ? callback.returnPath : "/join", { replace: true });
          return;
        }
        notifications.show({
          color: "red",
          title: AUTH_IDENTITY_COPY.appleSignInFailed,
          message: res.message ?? AUTH_IDENTITY_COPY.appleSignupFailed,
        });
        navigate("/login", { replace: true });
      } catch (err) {
        window.history.replaceState(null, "", window.location.pathname);
        const message = err instanceof ApiError ? err.message : err instanceof Error ? err.message : AUTH_IDENTITY_COPY.appleSignInFailed;
        notifications.show({ color: "red", title: AUTH_IDENTITY_COPY.appleSignInFailed, message });
        navigate("/login", { replace: true });
      }
    }

    void finish();
  }, [applyTokenUser, navigate, searchParams]);

  return (
    <AuthShell title="Signing in with Apple" subtitle="Please wait while we finish Sign in with Apple.">
      <Center mih="var(--axio-unit-120)">
        <Stack align="center" gap="sm">
          <AxioLoader />
          <Text size="sm" c="dimmed">
            Returning you to AxioHire…
          </Text>
        </Stack>
      </Center>
    </AuthShell>
  );
}
